import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  InfoOutlined as InfoOutlinedIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { adminFetch } from '../utils/adminFetch';
import { getAdminCourseName } from '../utils/courseDisplay';

const InfoTooltip: React.FC<{ title: React.ReactNode }> = ({ title }) => (
  <Tooltip title={title} arrow placement="top">
    <InfoOutlinedIcon
      fontSize="small"
      color="action"
      sx={{ ml: 0.5, cursor: 'help', verticalAlign: 'text-bottom' }}
      aria-label="Information"
    />
  </Tooltip>
);

const LabelWithTooltip: React.FC<{ label: string; tooltip: React.ReactNode }> = ({
  label,
  tooltip,
}) => (
  <Box component="span" display="inline-flex" alignItems="center">
    {label}
    <InfoTooltip title={tooltip} />
  </Box>
);

type ReminderPath = 'elearning' | 'blended';

type TemplateOption = {
  id: string;
  key: string;
  name: string;
  description: string;
  isAutomaticReminder?: boolean;
};

type TriggerRow = {
  key: string;
  enabled: boolean;
  templateId: string | null;
  effectiveTemplateId?: string | null;
  label: string;
  description: string;
  defaultTemplateKey: string;
  sortOrder: number;
};

type ReminderConfigResponse = {
  courseId: string;
  enabled: boolean;
  path: ReminderPath;
  includeMagicLink: boolean;
  customButtonUrl: string;
  triggers: TriggerRow[];
  templates: TemplateOption[];
};

const CourseReminderConfig: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [courseTitle, setCourseTitle] = useState('');
  const [config, setConfig] = useState<ReminderConfigResponse | null>(null);

  const loadData = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    setError(null);

    try {
      const [courseRes, configRes] = await Promise.all([
        adminFetch(`${apiBaseUrl}/courses/${courseId}`),
        adminFetch(`${apiBaseUrl}/courses/${courseId}/reminder-config`),
      ]);

      if (!courseRes.ok) {
        throw new Error('Formation introuvable');
      }
      if (!configRes.ok) {
        throw new Error('Configuration relances introuvable');
      }

      const courseData = await courseRes.json();
      const configData = (await configRes.json()) as ReminderConfigResponse;

      setCourseTitle(getAdminCourseName(courseData));
      setConfig(configData);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, courseId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const templatesById = useMemo(() => {
    const map = new Map<string, TemplateOption>();
    config?.templates.forEach((t) => map.set(t.id, t));
    return map;
  }, [config?.templates]);

  const handleSave = async () => {
    if (!courseId || !config) return;
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await adminFetch(`${apiBaseUrl}/courses/${courseId}/reminder-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: config.enabled,
          path: config.path,
          includeMagicLink: config.includeMagicLink,
          customButtonUrl: config.customButtonUrl,
          triggers: config.triggers.map((t) => ({
            key: t.key,
            enabled: t.enabled,
            templateId: t.templateId,
          })),
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || 'Erreur lors de la sauvegarde');
      }

      const saved = (await response.json()) as ReminderConfigResponse;
      setConfig(saved);
      setSuccess('Configuration enregistrée.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateTrigger = (key: string, patch: Partial<TriggerRow>) => {
    if (!config) return;
    setConfig({
      ...config,
      triggers: config.triggers.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    });
  };

  const handlePathChange = async (path: ReminderPath) => {
    if (!courseId || !config) return;
    setSaving(true);
    setError(null);

    try {
      const response = await adminFetch(`${apiBaseUrl}/courses/${courseId}/reminder-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path }),
      });

      if (!response.ok) {
        throw new Error('Impossible de changer le chemin');
      }

      const saved = (await response.json()) as ReminderConfigResponse;
      setConfig((prev) =>
        prev
          ? {
              ...saved,
              enabled: prev.enabled,
              includeMagicLink: prev.includeMagicLink,
              customButtonUrl: prev.customButtonUrl,
            }
          : saved
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!config) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Configuration introuvable'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/courses')}>
          Retour
        </Button>
        <EmailIcon color="primary" />
        <Box>
          <Typography variant="h5">Relances automatiques</Typography>
          <Typography variant="body2" color="text.secondary">
            {courseTitle}
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {!config.enabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Les relances sont désactivées pour cette formation : aucun email automatique ne partira,
          même si des types de relance sont cochés ci-dessous. Activez l&apos;interrupteur principal
          pour lancer les envois.
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.enabled}
                  onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                />
              }
              label={
                <LabelWithTooltip
                  label="Activer les relances automatiques pour cette formation"
                  tooltip={
                    <>
                      Interrupteur principal : tant qu&apos;il est désactivé, aucune relance ne
                      part. À la première activation, les réglages globaux (templates, magic link)
                      sont copiés sur cette formation. Modifier les defaults globaux ensuite
                      n&apos;impacte pas une formation déjà configurée.
                    </>
                  }
                />
              }
            />

            <FormControl fullWidth>
              <InputLabel id="reminder-path-label">Chemin email</InputLabel>
              <Select
                labelId="reminder-path-label"
                label="Chemin email"
                value={config.path}
                onChange={(e) => handlePathChange(e.target.value as ReminderPath)}
              >
                <MenuItem value="elearning">E-learning</MenuItem>
                <MenuItem value="blended">Blended</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                E-learning ou blended, indépendamment du type de formation.
                <InfoTooltip title="Choisissez le scénario de relances (inactivité en ligne vs rappels autour des ateliers). Ce réglage n'est pas lié au type « e-learning / blended » du parcours." />
              </Typography>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={config.includeMagicLink}
                  onChange={(e) =>
                    setConfig({ ...config, includeMagicLink: e.target.checked })
                  }
                />
              }
              label={
                <LabelWithTooltip
                  label="Inclure un magic link dans les emails"
                  tooltip="Si activé, le bouton de l'email connecte l'apprenant sans mot de passe. Sinon, utilisez l'URL personnalisée ci-dessous."
                />
              }
            />

            {!config.includeMagicLink && (
              <TextField
                label="URL du bouton (si magic link désactivé)"
                value={config.customButtonUrl}
                onChange={(e) => setConfig({ ...config, customButtonUrl: e.target.value })}
                fullWidth
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
        <Typography variant="h6">
          Emails ({config.path === 'elearning' ? 'E-learning' : 'Blended'})
        </Typography>
        <InfoTooltip
          title={
            <>
              Chaque type de relance peut être activé séparément, mais uniquement si
              l&apos;interrupteur principal est activé. Les préférences apprenant (opt-out global
              ou par formation) peuvent aussi bloquer l&apos;envoi.
            </>
          }
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Préparez les relances à utiliser une fois la formation activée. Les templates globaux sont
        pré-sélectionnés tant qu&apos;aucune surcharge n&apos;est enregistrée pour cette formation.
      </Typography>

      <Stack spacing={2}>
        {config.triggers.map((trigger) => (
          <Card key={trigger.key}>
            <CardContent>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                >
                  <Box>
                    <Typography variant="subtitle1">{trigger.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {trigger.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Template par défaut : {trigger.defaultTemplateKey}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={trigger.enabled}
                        disabled={!config.enabled}
                        onChange={(e) =>
                          updateTrigger(trigger.key, { enabled: e.target.checked })
                        }
                      />
                    }
                    label={
                      <LabelWithTooltip
                        label="Activé"
                        tooltip={
                          config.enabled ? (
                            <>
                              Ce type de relance sera évalué à chaque passage du worker (toutes les
                              heures) et envoyé si les conditions métier sont remplies.
                            </>
                          ) : (
                            <>
                              Inactif tant que l&apos;interrupteur principal est désactivé. Vous
                              pouvez cocher à l&apos;avance pour préparer la configuration.
                            </>
                          )
                        }
                      />
                    }
                  />
                </Stack>

                <FormControl fullWidth size="small">
                  <InputLabel id={`template-${trigger.key}`}>
                    Template
                  </InputLabel>
                  <Select
                    labelId={`template-${trigger.key}`}
                    label="Template"
                    value={trigger.templateId || trigger.effectiveTemplateId || ''}
                    onChange={(e) =>
                      updateTrigger(trigger.key, {
                        templateId: e.target.value ? String(e.target.value) : null,
                      })
                    }
                  >
                    <MenuItem value="">
                      Par défaut ({trigger.defaultTemplateKey})
                    </MenuItem>
                    {config.templates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}
                        {template.isAutomaticReminder || template.key.startsWith('auto_reminder_')
                          ? ' [Relance auto]'
                          : ''}{' '}
                        ({template.key})
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                    Surcharge optionnelle pour cette formation uniquement.
                    <InfoTooltip title="Sans surcharge enregistrée, c'est le template défini dans Emails → Relances automatiques (défauts) qui s'applique, puis le template système auto_reminder_* en secours." />
                  </Typography>
                </FormControl>

                {(trigger.templateId || trigger.effectiveTemplateId) &&
                  templatesById.get(trigger.templateId || trigger.effectiveTemplateId || '') && (
                  <Typography variant="caption" color="text.secondary">
                    {templatesById.get(trigger.templateId || trigger.effectiveTemplateId || '')?.description}
                    {!trigger.templateId && trigger.effectiveTemplateId && (
                      <> — défaut global</>
                    )}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Box mt={3} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          Enregistrer
        </Button>
      </Box>
    </Container>
  );
};

export default CourseReminderConfig;
